import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { AppIcon } from '../../../components/ui/icon'
import { mdiFilePlus, mdiPlaylistCheck, mdiPlus, mdiRefresh } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { HierarchyTree } from '../../../components/hierarchy/HierarchyTree'
import type { HierarchyMove, HierarchyTreeNode } from '../../../components/hierarchy/HierarchyTree'
import { computeOutlineNumbers } from '../../../components/hierarchy/outline-numbering'
import {
    ContractTypeEditor,
    NONE_OPTION,
} from '../../../components/contract-types/ContractTypeEditor'
import type { ContractTypeUpdatePatch } from '../../../components/contract-types/ContractTypeEditor'
import {
    contractTypeDisplayName,
    documentTypeDisplayName,
} from '../../../components/contract-types/display-names'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useLocale } from '../../../contexts/LocaleContext'
import { apiClient } from '../../../api/client'
import type { components } from '../../../api/schema'
import { cn } from '../../../lib/utils'
import { apiErrorMessage } from '../../../lib/apiError'
import { Can, useCan } from '../../../contexts/PermissionsContext'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { notify } from '../../../lib/notifications'

const contractTypesSearchSchema = z.object({
    contract_type_uid: z.string().optional(),
})

export const Route = createFileRoute('/$org/contract-types/')({
    component: ContractTypesPage,
    validateSearch: contractTypesSearchSchema,
})

type ContractType = components['schemas']['ContractType']
type CreateContractTypeRequest = components['schemas']['CreateContractTypeRequest']
type UpdateContractTypeRequest = components['schemas']['UpdateContractTypeRequest']
type DocumentType = components['schemas']['DocumentType']

function ContractTypesPage() {
    const { org } = useParams({ from: '/$org/contract-types/' })
    const navigate = useNavigate({ from: '/$org/contract-types/' })
    const search = useSearch({ from: '/$org/contract-types/' })
    const { t } = useTranslation('types')
    // Contract-type names are `{en, fr}` maps: every label below is resolved
    // against the active locale, which is therefore a dependency of each memo
    // so a language switch re-labels the tree live.
    const { locale } = useLocale()
    const queryClient = useQueryClient()
    // The contract-type catalogue is org configuration. A reader keeps the whole
    // tree and the detail pane; only the affordances that create, re-parent or
    // delete a type go away (spec 2026-09-04-02 §2 "Gating").
    const canWrite = useCan('configuration.write')

    const selectedUid = search.contract_type_uid || null

    const [isEditingTree, setIsEditingTree] = useState(false)
    const [mobilePane, setMobilePane] = useState<'tree' | 'detail'>('tree')
    const [pageError, setPageError] = useState<string | null>(null)
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [newContractType, setNewContractType] = useState({
        code: '',
        names: { en: '', fr: '' },
        documentTypeUid: '',
        parentUid: '',
    })

    const contractTypesQueryKey = useMemo(() => ['contractTypesHierarchy', org], [org])

    const {
        data: contractTypes = [],
        isLoading: isContractTypesLoading,
        error: contractTypesError,
        refetch: refetchContractTypes,
    } = useQuery({
        queryKey: contractTypesQueryKey,
        queryFn: async (): Promise<ContractType[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/contract-types', {
                params: { path: { orgCode: org }, query: { limit: 1000 } },
            })
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to load contract types'))
            return (response.data as { data?: ContractType[] })?.data || []
        },
    })

    const { data: documentTypes = [] } = useQuery({
        queryKey: ['documentTypes', org],
        queryFn: async (): Promise<DocumentType[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/document-types', {
                params: { path: { orgCode: org }, query: { limit: 1000 } },
            })
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to load document types'))
            return (response.data as { data?: DocumentType[] })?.data || []
        },
    })

    useBreadcrumbs([
        { title: t('breadcrumb.home'), path: `/${org}` },
        { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
        { title: t('contract_types.title') },
    ])

    const invalidateContractTypes = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: contractTypesQueryKey })
        // The read-only useContractTypes() hook used elsewhere (contracts
        // list/detail forms, asset contacts) goes through openapi-react-query
        // and therefore has a different key shape — bust it too so those
        // pages see the family/code/parent change without a hard reload.
        queryClient.invalidateQueries({
            predicate: query => {
                const key = query.queryKey as unknown[]
                return (
                    Array.isArray(key) &&
                    key.length >= 2 &&
                    key[0] === 'get' &&
                    key[1] === '/api/v1/organizations/{orgCode}/contract-types'
                )
            },
        })
    }, [queryClient, contractTypesQueryKey])

    const selectContractType = useCallback(
        (uid: string | undefined) => {
            navigate({ search: prev => ({ ...prev, contract_type_uid: uid }) })
            if (uid) setMobilePane('detail')
        },
        [navigate]
    )

    // Raw PATCH used by the drag & drop handler, which reverts the optimistic
    // move on a backend self/cycle rejection.
    const patchContractType = useCallback(
        async (uid: string, data: UpdateContractTypeRequest) => {
            const response = await apiClient.PATCH(
                '/api/v1/organizations/{orgCode}/contract-types/{contractTypeUid}',
                { params: { path: { orgCode: org, contractTypeUid: uid } }, body: data }
            )
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to update contract type'))
            return response.data
        },
        [org]
    )

    const createContractTypeMutation = useMutation({
        mutationFn: async (data: CreateContractTypeRequest) => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/contract-types', {
                params: { path: { orgCode: org } },
                body: data,
            })
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to create contract type'))
            return response.data as ContractType
        },
        onSuccess: created => {
            invalidateContractTypes()
            setIsCreateDialogOpen(false)
            setNewContractType({ code: '', names: { en: '', fr: '' }, documentTypeUid: '', parentUid: '' })
            if (created?.uid) selectContractType(created.uid)
        },
        onError: error => setPageError(t('contract_types.editor.create_error') + ' ' + error.message),
    })

    const updateContractTypeMutation = useMutation({
        mutationFn: ({ uid, data }: { uid: string; data: UpdateContractTypeRequest }) =>
            patchContractType(uid, data),
        onSuccess: () => invalidateContractTypes(),
    })

    // « Liste usuelle »: restores the shipped expected_on of every shipped
    // contract type, keeping the organization's additions (spec 2026-10-11-16).
    const restoreUsualSetMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.POST(
                '/api/v1/organizations/{orgCode}/contract-types/restore-expected-on',
                { params: { path: { orgCode: org } } }
            )
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to restore the usual list'))
            return response.data
        },
        onSuccess: data => {
            invalidateContractTypes()
            notify({
                key: 'contract_types_usual_set',
                variant: 'default',
                titleKey: 'types:contract_types.usual_set.done',
                values: { count: String(data?.updated.length ?? 0) },
            })
        },
        onError: error => setPageError(t('contract_types.usual_set.error') + ' ' + error.message),
    })

    const deleteContractTypeMutation = useMutation({
        mutationFn: async (uid: string) => {
            const response = await apiClient.DELETE(
                '/api/v1/organizations/{orgCode}/contract-types/{contractTypeUid}',
                { params: { path: { orgCode: org, contractTypeUid: uid } } }
            )
            if (response.error) throw new Error(apiErrorMessage(response.error, 'Failed to delete contract type'))
            return response.data
        },
        onSuccess: () => {
            invalidateContractTypes()
            selectContractType(undefined)
            setMobilePane('tree')
        },
    })

    const documentTypeName = useCallback(
        (uid: string) => {
            const dt = documentTypes.find(d => d.uid === uid)
            return dt ? documentTypeDisplayName(dt, locale) : undefined
        },
        [documentTypes, locale]
    )

    const treeNodes: HierarchyTreeNode[] = useMemo(
        () =>
            contractTypes.map(contractType => ({
                uid: contractType.uid,
                label: contractTypeDisplayName(contractType, locale),
                parentUid: contractType.parent_contract_type_uid || null,
                badge: contractType.document_type_uid ? documentTypeName(contractType.document_type_uid) : undefined,
            })),
        [contractTypes, documentTypeName, locale]
    )

    const selectedContractType = useMemo(
        () => contractTypes.find(ct => ct.uid === selectedUid) || null,
        [contractTypes, selectedUid]
    )

    // Same helper as the tree, so the detail pane shows the very same number.
    // Contract types are not `orderable` (no persisted ordering column), so
    // this only reflects the alphabetical rendering order — same as the tree.
    const outlineNumbers = useMemo(() => computeOutlineNumbers(treeNodes, false), [treeNodes])

    // No ordering column/reorder endpoint exists for contract types (unlike
    // folder definitions), so a move is a single re-parent PATCH — or an
    // explicit `null` to promote to root, per the tri-state PATCH convention.
    // A backend self/cycle rejection (ErrInvalidParentContractTypeSelf /
    // ErrInvalidParentContractTypeCycle) surfaces as the same non-blocking
    // page-error banner the folder editor uses, and the invalidation below
    // reverts the optimistic tree state back to the server's.
    const handleMove = useCallback(
        async (move: HierarchyMove) => {
            setPageError(null)
            const currentParentUid = contractTypes.find(ct => ct.uid === move.uid)?.parent_contract_type_uid ?? null
            if (currentParentUid === move.parentUid) return
            try {
                await patchContractType(move.uid, { parent_contract_type_uid: move.parentUid })
            } catch (e) {
                setPageError(t('contract_types.editor.move_error') + ' ' + (e instanceof Error ? e.message : ''))
            } finally {
                invalidateContractTypes()
            }
        },
        [contractTypes, patchContractType, invalidateContractTypes, t]
    )

    // One inline field commit = one partial PATCH. Absent fields are left
    // untouched by the endpoint (tri-state, see UpdateContractTypeRequest),
    // so no read-modify-write of the other fields is needed here.
    const handleUpdate = useCallback(
        async (data: ContractTypeUpdatePatch) => {
            setPageError(null)
            await updateContractTypeMutation.mutateAsync({ uid: selectedContractType!.uid, data })
        },
        [selectedContractType, updateContractTypeMutation]
    )

    const handleDelete = useCallback(async () => {
        setPageError(null)
        await deleteContractTypeMutation.mutateAsync(selectedContractType!.uid)
    }, [selectedContractType, deleteContractTypeMutation])

    const openCreateDialog = useCallback((parentUid: string | null) => {
        // Belt and braces: the affordances are hidden, the dialog stays shut.
        if (!canWrite) return
        setNewContractType({ code: '', names: { en: '', fr: '' }, documentTypeUid: '', parentUid: parentUid || '' })
        setIsCreateDialogOpen(true)
    }, [canWrite])

    const createParentOptions = useMemo(
        () => [
            { value: NONE_OPTION, label: t('contract_types.no_parent') },
            ...contractTypes
                .map(ct => ({ value: ct.uid, label: contractTypeDisplayName(ct, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [contractTypes, locale, t]
    )

    const createDocumentTypeOptions = useMemo(
        () => [
            { value: NONE_OPTION, label: t('contract_types.no_document_type') },
            ...documentTypes
                .map(dt => ({ value: dt.uid, label: documentTypeDisplayName(dt, locale) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [documentTypes, locale, t]
    )

    const submitCreate = () => {
        createContractTypeMutation.mutate({
            code: newContractType.code,
            names: {
                ...(newContractType.names.en ? { en: newContractType.names.en } : {}),
                ...(newContractType.names.fr ? { fr: newContractType.names.fr } : {}),
            },
            document_type_uid: newContractType.documentTypeUid || undefined,
            parent_contract_type_uid: newContractType.parentUid || undefined,
        })
    }

    if (contractTypesError) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <p className="text-destructive">
                        {t('contract_types.error_loading', { message: (contractTypesError as Error).message })}
                    </p>
                    <Button onClick={() => refetchContractTypes()} className="mt-4" data-testid="contract-types-retry">
                        <AppIcon path={mdiRefresh} size="md" className="mr-2" />
                        {t('contract_types.retry_button')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex h-screen flex-col">
            {/* Header */}
            <div className="border-b p-4">
                <PageHeader
                    icon={SECTION_ICONS.contractTypes}
                    title={t('contract_types.title')}
                    description={
                        <span className="hidden md:block">{t('contract_types.subtitle')}</span>
                    }
                    actions={
                    <>
                        <Button
                            onClick={() => void refetchContractTypes()}
                            variant="outline"
                            aria-label={t('contract_types.refresh_button')}
                            title={t('contract_types.refresh_button')}
                            data-testid="refresh-contract-types"
                        >
                            <AppIcon path={mdiRefresh} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('contract_types.refresh_button')}</span>
                        </Button>
                        <Can perm="configuration.write">
                            <Button
                                onClick={() => restoreUsualSetMutation.mutate()}
                                variant="outline"
                                disabled={restoreUsualSetMutation.isPending}
                                aria-label={t('contract_types.usual_set.button')}
                                title={t('contract_types.usual_set.title')}
                                data-testid="contract-types-usual-set"
                            >
                                <AppIcon path={mdiPlaylistCheck} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('contract_types.usual_set.button')}</span>
                            </Button>
                            <Button
                                onClick={() => openCreateDialog(null)}
                                aria-label={t('contract_types.create')}
                                title={t('contract_types.create')}
                                data-testid="create-contract-type-button"
                            >
                                <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('contract_types.create')}</span>
                            </Button>
                        </Can>
                    </>
                    }
                />

                {pageError && (
                    <div
                        className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive"
                        role="status"
                        data-testid="contract-types-page-error"
                    >
                        {pageError}
                    </div>
                )}

                {/* Mobile pane switch — the master-detail split collapses below md. */}
                <div className="mt-3 flex gap-2 md:hidden" data-testid="contract-types-pane-switch">
                    <Button
                        variant={mobilePane === 'tree' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setMobilePane('tree')}
                        data-testid="contract-types-pane-tree"
                    >
                        {t('contract_types.view.tree')}
                    </Button>
                    <Button
                        variant={mobilePane === 'detail' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => setMobilePane('detail')}
                        data-testid="contract-types-pane-detail"
                    >
                        {t('contract_types.view.details')}
                    </Button>
                </div>
            </div>

            {/* Master-detail */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div
                    className={cn(
                        'min-h-0 w-full overflow-hidden border-r md:block md:w-1/3',
                        mobilePane === 'tree' ? 'block' : 'hidden'
                    )}
                >
                    {isContractTypesLoading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            {t('contract_types.loading')}
                        </div>
                    ) : (
                        <HierarchyTree
                            className="h-full"
                            title={t('contract_types.tree.title', { count: contractTypes.length })}
                            nodes={treeNodes}
                            selectedUid={selectedUid}
                            onSelect={selectContractType}
                            editable={canWrite}
                            editing={isEditingTree}
                            onEditingChange={setIsEditingTree}
                            // No `ordering` column/reorder endpoint exists for
                            // contract types — siblings render alphabetically
                            // and drag & drop only re-parents (same mode
                            // DocumentTypesTree already uses).
                            orderable={false}
                            onMove={canWrite ? handleMove : undefined}
                            onCreateChild={canWrite ? openCreateDialog : undefined}
                            defaultExpanded="all"
                            numbered
                            testIdPrefix="contract-types"
                            labels={{
                                edit: t('contract_types.tree.edit_mode'),
                                done: t('contract_types.tree.edit_mode_done'),
                                root: t('contract_types.root_contract_type'),
                                dropToRoot: t('contract_types.drop_to_root'),
                                empty: t('contract_types.tree.empty'),
                                addChild: t('contract_types.tree.add_child'),
                            }}
                            headerActions={
                                canWrite ? (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => openCreateDialog(selectedUid)}
                                        aria-label={t('contract_types.tree.add_child')}
                                        title={t('contract_types.tree.add_child')}
                                        data-testid="contract-types-tree-add"
                                    >
                                        <AppIcon path={mdiFilePlus} size="sm" />
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}
                </div>

                <div
                    className={cn(
                        'min-h-0 w-full flex-1 overflow-y-auto md:block',
                        mobilePane === 'detail' ? 'block' : 'hidden'
                    )}
                >
                    <ContractTypeEditor
                        org={org}
                        contractType={selectedContractType}
                        number={selectedContractType ? outlineNumbers.get(selectedContractType.uid) : undefined}
                        allContractTypes={contractTypes}
                        documentTypes={documentTypes}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                        onSelectContractType={selectContractType}
                        isDeleting={deleteContractTypeMutation.isPending}
                    />
                </div>
            </div>

            {/* Create dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent data-testid="create-contract-type-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('contract_types.create_dialog.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 p-6 pt-2">
                        <div>
                            <Label htmlFor="new-contract-type-code">{t('contract_types.create_dialog.code_label')}</Label>
                            <Input
                                id="new-contract-type-code"
                                placeholder={t('contract_types.placeholder_code')}
                                value={newContractType.code}
                                onChange={e => setNewContractType(prev => ({ ...prev, code: e.target.value }))}
                                data-testid="create-contract-type-code"
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <Label htmlFor="new-contract-type-name-en">
                                    🇺🇸 {t('contract_types.create_dialog.name_en_label')}
                                </Label>
                                <Input
                                    id="new-contract-type-name-en"
                                    value={newContractType.names.en}
                                    onChange={e =>
                                        setNewContractType(prev => ({
                                            ...prev,
                                            names: { ...prev.names, en: e.target.value },
                                        }))
                                    }
                                    data-testid="create-contract-type-name-en"
                                />
                            </div>
                            <div>
                                <Label htmlFor="new-contract-type-name-fr">
                                    🇫🇷 {t('contract_types.create_dialog.name_fr_label')}
                                </Label>
                                <Input
                                    id="new-contract-type-name-fr"
                                    value={newContractType.names.fr}
                                    onChange={e =>
                                        setNewContractType(prev => ({
                                            ...prev,
                                            names: { ...prev.names, fr: e.target.value },
                                        }))
                                    }
                                    data-testid="create-contract-type-name-fr"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>{t('contract_types.create_dialog.parent_label')}</Label>
                            <SearchableSelect
                                options={createParentOptions}
                                value={newContractType.parentUid || NONE_OPTION}
                                onValueChange={value =>
                                    setNewContractType(prev => ({
                                        ...prev,
                                        parentUid: value === NONE_OPTION || value === '' ? '' : value,
                                    }))
                                }
                                placeholder={t('contract_types.select_parent_placeholder')}
                                searchPlaceholder={t('contract_types.editor.search_placeholder')}
                                emptyMessage={t('contract_types.editor.no_results')}
                                data-testid="create-contract-type-parent"
                            />
                        </div>

                        <div>
                            <Label>{t('contract_types.create_dialog.document_type_label')}</Label>
                            <SearchableSelect
                                options={createDocumentTypeOptions}
                                value={newContractType.documentTypeUid || NONE_OPTION}
                                onValueChange={value =>
                                    setNewContractType(prev => ({
                                        ...prev,
                                        documentTypeUid: value === NONE_OPTION || value === '' ? '' : value,
                                    }))
                                }
                                placeholder={t('contract_types.select_document_type_placeholder')}
                                searchPlaceholder={t('contract_types.editor.search_placeholder')}
                                emptyMessage={t('contract_types.editor.no_results')}
                                data-testid="create-contract-type-document-type"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsCreateDialogOpen(false)}
                                disabled={createContractTypeMutation.isPending}
                                data-testid="create-contract-type-cancel"
                            >
                                {t('contract_types.create_dialog.cancel')}
                            </Button>
                            <Button
                                onClick={submitCreate}
                                disabled={
                                    createContractTypeMutation.isPending ||
                                    !newContractType.code ||
                                    (!newContractType.names.en && !newContractType.names.fr)
                                }
                                data-testid="create-contract-type-submit"
                            >
                                {createContractTypeMutation.isPending
                                    ? t('contract_types.create_dialog.creating')
                                    : t('contract_types.create_dialog.submit')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
